import { getCsrfToken } from "@/integrations/php/client";
import type { LeadStageType, ServiceType } from "@/context/LeadContext";

export interface AdminNetworkMapLead {
  id: string;
  service: ServiceType;
  postalCode: string;
  stage: LeadStageType | "";
  latitude: number;
  longitude: number;
  createdAt: string | null;
}

export interface AdminNetworkMapSummary {
  unassignedTotal: number;
  unassignedMappable: number;
  unassignedWithoutCoordinates: number;
  oldestUnassignedAt: string | null;
  mappableTruncated: boolean;
}

export interface AdminNetworkMapData {
  summary: AdminNetworkMapSummary;
  unassignedLeads: AdminNetworkMapLead[];
  generatedAt: string;
}

const toNumber = (value: unknown): number => Number(value || 0);
const nullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

export async function fetchAdminNetworkMap(): Promise<AdminNetworkMapData> {
  const response = await fetch("/api/admin/network-map.php", {
    credentials: "include",
    headers: { "X-CSRF-Token": await getCsrfToken() },
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(body?.error?.message || "Δεν φορτώθηκαν τα δεδομένα του χάρτη.");
  }

  const data = body?.data || {};
  const summary = data.summary || {};

  return {
    summary: {
      unassignedTotal: toNumber(summary.unassigned_total),
      unassignedMappable: toNumber(summary.unassigned_mappable),
      unassignedWithoutCoordinates: toNumber(summary.unassigned_without_coordinates),
      oldestUnassignedAt: nullableString(summary.oldest_unassigned_at),
      mappableTruncated: Boolean(summary.mappable_truncated),
    },
    unassignedLeads: Array.isArray(data.unassigned_leads)
      ? data.unassigned_leads.map((row: Record<string, unknown>) => ({
          id: String(row.id || ""),
          service: String(row.service || "άλλο") as ServiceType,
          postalCode: String(row.postal_code || ""),
          stage: String(row.stage || "") as LeadStageType | "",
          latitude: toNumber(row.latitude),
          longitude: toNumber(row.longitude),
          createdAt: nullableString(row.created_at),
        }))
      : [],
    generatedAt: String(data.generated_at || ""),
  };
}
